import { useState, forwardRef } from "react";
import { Eye, EyeOff } from "lucide-react";

/**
 * Password input with a show/hide eye toggle.
 *
 * Drop-in replacement for `<input type="password" />`. Forwards refs and
 * accepts every standard input prop (value, onChange, autoComplete, etc.).
 * The eye button is positioned inside the field on the right.
 *
 * Two visual variants:
 *   - `variant="line"` (default) — borderless field with a bottom border,
 *     matches the auth screens.
 *   - `variant="box"` — bordered box, matches the admin forms.
 */
const PasswordInput = forwardRef(function PasswordInput(
  {
    variant = "line",
    className = "",
    wrapperClassName = "",
    eyeTestId,
    ...rest
  },
  ref
) {
  const [visible, setVisible] = useState(false);

  const base =
    variant === "box"
      ? "w-full bg-transparent border border-[var(--border)] focus:border-[var(--accent)] outline-none px-3 py-2 pr-10 font-mono-ui text-xs transition-colors"
      : "w-full bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)] outline-none py-2 pr-10 transition-colors";

  return (
    <div className={`relative block ${wrapperClassName}`}>
      <input
        {...rest}
        ref={ref}
        type={visible ? "text" : "password"}
        className={`${base} ${className}`}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Hide password" : "Show password"}
        title={visible ? "Hide password" : "Show password"}
        data-testid={eyeTestId || "password-toggle"}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-dim)] hover:text-[var(--accent)] transition-colors p-1"
        tabIndex={-1}
      >
        {visible ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
});

export default PasswordInput;
